import {observable} from 'mobx';
import {Channel} from '../types/Channel';
import * as ChannelAPI from '../apis/ChannelAPI';
import * as AbTestAPI from '../apis/AbTestAPI';
import {DateRange} from './TimeIntervalStore';
import {HistoryHandler, HistoryItem} from './MemoryBarStore';
import _ from 'lodash';
import {AsyncStorage, channelKey} from '../storage/storage';
import configString from '../utility/Config';
import {DateUtils} from '../utility/DateUtils';
import moment from 'moment';

interface AbtestImportCheck {
    channel: string;
    testId: string;
    lastImportedAbTestDay: number;
    lastAvailableAbTestEvent: number;
}

export enum ChannelStatus {
    Loading = 'loading',
    Available = 'available',
}

export interface AbTestWithVariants {
    testId: string;
    name?: string;
    variantIds: string[];
    importNeeded?: boolean;
    lastImported?: moment.Moment;
}

export interface AbtestsForChannel {
    channelName: string;
    abTestsWithVariants: AbTestWithVariants[];
}

export interface ChannelState {
    availableChannels: Channel[];
    selectedChannel?: Channel;
    channelLoadingStatus: ChannelStatus;
    setSelectedChannel: (channel: Channel | string) => void;
    setSelectedChannelWithoutReload: (channel: Channel | string) => void;
    loadChannelsForDateRange: (mainDateRange: DateRange, forceTriggerChannelChange?: boolean) => void;
    setCurrenciesForChannel: (channelsWithChangedCurrency: Channel[]) => void;
    getAllChannelCurrencies: () => string[];
    getFullChannelForChannelName: (channelName: string) => Channel;
    abTestsForChannels: AbtestsForChannel[];
    setLastImportedDateForAbTest: (channelName: string, testId: string, lastImported?: moment.Moment) => void;
    getNameForAbtestId: (abTestId: string, channelName: string) => string | undefined;
}

export default class ChannelStore implements ChannelState, HistoryHandler {
    @observable public availableChannels: Channel[] = [];
    @observable public selectedChannel?: Channel;
    @observable public channelLoadingStatus: ChannelStatus = ChannelStatus.Loading;
    @observable public abTestsForChannels: AbtestsForChannel[] = [];
    private dateRangeForCurrentChannels?: DateRange;

    constructor(
        private selectedChannelChanged: () => void,
        private availableChannelsChanged: () => void,
        private readonly storage: AsyncStorage,
        private readonly getMainDateRange: () => DateRange,
        private readonly getChannelNameViaUrl: () => string,
        private readonly setChannelToUrl: (channelName: string) => void
    ) {
        const urlChannelName = this.getChannelNameViaUrl();
        if (urlChannelName !== '') {
            this.selectedChannel = {
                name: urlChannelName,
                currency: 'EUR',
            };
        }
    }

    public getNameForAbtestId = (abTestId: string, channelName: string): string | undefined => {
        const abTestForChannel = this.abTestsForChannels.find((abForChannel) => abForChannel.channelName === channelName);
        if (!abTestForChannel) {
            return undefined;
        }
        const matchingAbtest = abTestForChannel.abTestsWithVariants.find((abTest) => abTest.testId === abTestId);
        return matchingAbtest ? matchingAbtest.name : undefined;
    };

    public getAllChannelCurrencies = (): string[] => {
        if (this.channelLoadingStatus === ChannelStatus.Loading) {
            return [];
        }
        return _.uniq(this.availableChannels.map((channel) => channel.currency));
    };

    public setCurrenciesForChannel = (channelsWithChangedCurrencies: Channel[]) => {
        this.availableChannels = channelsWithChangedCurrencies;
        this.ensureActiveChannel();
        this.availableChannels.forEach((channel) => this.storage.setItem(channelKey(channel.name, 'currency'), {currencyCode: channel.currency}));
        this.availableChannelsChanged();
        this.selectedChannelChanged();
    };

    public loadChannelsForDateRange(dateRange: DateRange, forceTriggerChannelChange = false) {
        if (this.dateRangeForCurrentChannels && _.isEqual(dateRange, this.dateRangeForCurrentChannels)) {
            if (forceTriggerChannelChange) {
                this.selectedChannelChanged();
            }
            return;
        }
        this.dateRangeForCurrentChannels = dateRange;
        this.channelLoadingStatus = ChannelStatus.Loading;
        this.availableChannels = [];
        ChannelAPI.activeChannelsByDateRange({
            interval: dateRange,
        })
            .then((activeChannelResp) => {
                this.availableChannels = activeChannelResp.data.map((channel) => ({
                    name: channel.channel,
                    currency: channel.currency,
                }));
                this.ensureActiveChannel();
                this.setAbtestsForSelectedChannel(dateRange);
                this.channelLoadingStatus = ChannelStatus.Available;
                this.availableChannelsChanged();
                this.selectedChannelChanged();
            })
            .catch(() => {
                this.ensureActiveChannel();
                this.setAbtestsForSelectedChannel(dateRange);
                this.channelLoadingStatus = ChannelStatus.Available;
                this.availableChannelsChanged();
                this.selectedChannelChanged();
            });
    }

    public setSelectedChannelWithoutReload = (channel?: Channel | string) => {
        if (channel !== undefined) {
            if (typeof channel === 'string') {
                this.selectedChannel = this.getFullChannelForChannelName(channel);
                this.setChannelToUrl(channel);
            } else {
                this.selectedChannel = channel;
                this.setChannelToUrl(channel.name);
            }
        }
    };

    public setSelectedChannel = (channel?: Channel | string) => {
        this.setSelectedChannelWithoutReload(channel);
        if (channel !== undefined) {
            this.selectedChannelChanged();
        }
    };

    public getPartialHistory = (): Partial<HistoryItem> => {
        return {
            channel: this.selectedChannel ? this.selectedChannel.name : undefined,
        };
    };

    public applyHistory = (history: HistoryItem): void => {
        this.setSelectedChannel(history.channel);
    };

    private getCurrencyForChannel(channelName: string) {
        if (this.channelLoadingStatus === ChannelStatus.Loading) {
            return 'EUR';
        }

        const matchingChannel = this.availableChannels.find((availableChannel) => availableChannel.name === channelName);
        if (matchingChannel) {
            return matchingChannel.currency;
        }
        return 'EUR';
    }

    public getFullChannelForChannelName = (channelName: string): Channel => {
        return {
            name: channelName,
            currency: this.getCurrencyForChannel(channelName),
        };
    };

    private getDefaultChannel(): Channel | undefined {
        const envChannel = configString('ANALYTICS_CHANNEL');
        if (envChannel !== '') {
            const envChannelMatch = this.availableChannels.find((channel) => channel.name === envChannel);
            if (envChannelMatch) {
                return envChannelMatch;
            }
        }
        return this.availableChannels.length > 0 ? this.availableChannels[0] : undefined;
    }

    private ensureActiveChannel() {
        if (this.availableChannels.length === 0) {
            this.selectedChannel = undefined;
            return;
        }

        const selectedChannel = this.selectedChannel;
        const defaultChannel = this.getDefaultChannel();

        if (selectedChannel === undefined) {
            this.selectedChannel = defaultChannel;
            return;
        }

        const availableChannel = this.availableChannels.find((channel) => channel.name === selectedChannel.name);
        if (availableChannel) {
            this.selectedChannel = availableChannel;
            return;
        }

        this.selectedChannel = defaultChannel;
    }

    private setAbtestsForSelectedChannel = (dateRange?: DateRange): void => {
        const dateRangeToUse = dateRange || this.getMainDateRange();
        this.abTestsForChannels = [];
        const availableChannels = this.availableChannels;
        Promise.all(
            availableChannels.map((channel) =>
                AbTestAPI.abTestsAndVariantsPerChannel({
                    channel: channel.name,
                    interval: DateUtils.toDailyDateRange(dateRangeToUse),
                })
            )
        )
            .then((resps) => {
                this.abTestsForChannels = resps.map((resp, index) => ({
                    channelName: availableChannels[index].name,
                    abTestsWithVariants: resp.data.map((testData) => ({
                        testId: testData.testId,
                        name: testData.name,
                        variantIds: testData.variantIds,
                    })),
                }));
                this.checkForAbtestImports(this.abTestsForChannels, dateRangeToUse);
            })
            .catch(() => {
                /* noop */
            });
    };

    private getAbtestCheckTimestamps(channelName: string, testId: string): Promise<AbtestImportCheck | undefined> {
        return Promise.all([
            AbTestAPI.getLastImportedDay({channel: channelName, testId}),
            AbTestAPI.getLastAvailableAbtestEventTimestamp({channel: channelName, testId}),
        ])
            .then((resps) => {
                return {
                    channel: channelName,
                    testId,
                    lastImportedAbTestDay: resps[0].data,
                    lastAvailableAbTestEvent: resps[1].data,
                };
            })
            .catch(() => undefined);
    }

    private abTestImportNeeded = (lastAvailableAbTestEvent: number, lastImportedAbTestDay: number, dateRange: DateRange): boolean => {
        if (!dateRange.startDate || !dateRange.endDate) {
            return false;
        }
        const lastAvailableEventDay = moment(lastAvailableAbTestEvent, 'x').startOf('day');
        const lastImportedDay = moment(lastImportedAbTestDay, 'x').startOf('day');

        if (lastImportedDay.isBefore(dateRange.endDate) && lastImportedDay.isSame(lastAvailableEventDay)) {
            return false;
        } else {
            return !lastImportedDay.isAfter(dateRange.endDate);
        }
    };

    public setLastImportedDateForAbTest = (channelName: string, testId: string, lastImported?: moment.Moment): void => {
        if (!lastImported) {
            return;
        }
        this.abTestsForChannels = this.abTestsForChannels.map((abTestForChannel) => ({
            ...abTestForChannel,
            abTestsWithVariants: abTestForChannel.abTestsWithVariants.map((abTestWithVariants) => {
                const matchesUpdate = abTestForChannel.channelName === channelName && abTestWithVariants.testId === testId;
                return {
                    ...abTestWithVariants,
                    lastImported: matchesUpdate ? lastImported : abTestWithVariants.lastImported,
                    importNeeded: matchesUpdate ? false : abTestWithVariants.importNeeded,
                };
            }),
        }));
    };

    private checkForAbtestImports = (abtestsForChannels: AbtestsForChannel[], dateRange: DateRange) => {
        Promise.all(
            abtestsForChannels.map((abTestChannel) =>
                Promise.all(
                    abTestChannel.abTestsWithVariants.map((abtest) => this.getAbtestCheckTimestamps(abTestChannel.channelName, abtest.testId))
                )
            )
        ).then((resps) => {
            const abTestCheckResults = resps.reduce((acc: AbtestImportCheck[], channelAbTestChecks: Array<AbtestImportCheck | undefined>) => {
                return [...acc, ...(channelAbTestChecks || [])];
            }, []) as AbtestImportCheck[];

            this.abTestsForChannels = this.abTestsForChannels.map((abTestForChannel) => ({
                ...abTestForChannel,
                abTestsWithVariants: abTestForChannel.abTestsWithVariants.map((abTestWithVariants) => {
                    const resultForChannelAndTest = abTestCheckResults.find(
                        (checkResult) => checkResult.testId === abTestWithVariants.testId && checkResult.channel === abTestForChannel.channelName
                    );
                    const importNeeded =
                        resultForChannelAndTest &&
                        resultForChannelAndTest.channel === abTestForChannel.channelName &&
                        resultForChannelAndTest.testId === abTestWithVariants.testId &&
                        this.abTestImportNeeded(
                            resultForChannelAndTest.lastAvailableAbTestEvent,
                            resultForChannelAndTest.lastImportedAbTestDay,
                            dateRange
                        );
                    return {
                        ...abTestWithVariants,
                        lastImported: resultForChannelAndTest ? moment(resultForChannelAndTest.lastImportedAbTestDay, 'x') : moment(0),
                        importNeeded,
                    };
                }),
            }));
        });
    };
}



// WEBPACK FOOTER //
// ./src/stores/ChannelStore.ts